/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { ASYNC_AUTOCOMPLETE_TYPES, DataActionConditionType } from 'cio/utils/campaigns/data-action';
import { task, hash } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import AutosuggestService from 'cio/services/autosuggest';
import LogModel from 'cio/models/log';
import { extractKeyPathsWithIndices, keyPathToString } from 'cio/utils/objects';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import CustomerModel from 'cio/models/customer';
import EnvironmentService from 'cio/services/environment';

interface ActionComposerDataActionAutocompleteValuesArgs {
  campaign: CampaignModel;
  selectedCustomer?: CustomerModel;
  selectedEvent?: LogModel;
  selectedTrigger?: BroadcastTriggerModel;
  supportedConditionTypes: DataActionConditionType[];
  splitEventAttributesByDot?: boolean;
}

function extractLogAutocompleteValues(log: LogModel | null | undefined) {
  if (log == null) {
    return [];
  }

  let paths = extractKeyPathsWithIndices(log.attrs);

  return paths.map((keys) => {
    let value = keyPathToString(keys);

    return {
      label: value,
      value,
      path: keys,
    };
  });
}

function extractBroadcastTriggerAutocompleteValues(trigger: BroadcastTriggerModel | null | undefined) {
  if (trigger == null) {
    return [];
  }

  try {
    let data = JSON.parse(trigger.triggerData);
    let paths = extractKeyPathsWithIndices(data);

    return paths.map((keys) => {
      let value = keyPathToString(keys);

      return {
        label: value,
        value,
        path: keys,
      };
    });
  } catch {
    return [];
  }
}

function transformValuesToAutocompleteValues(attributes: string[], splitByDot = false) {
  return attributes.map((a) => keyPathToString([a], splitByDot));
}

export default class ActionComposerDataActionAutocompleteValuesComponent extends Component<ActionComposerDataActionAutocompleteValuesArgs> {
  @service autosuggest!: AutosuggestService;
  @service environment!: EnvironmentService;

  constructor(
    owner: ActionComposerDataActionAutocompleteValuesComponent,
    args: ActionComposerDataActionAutocompleteValuesArgs,
  ) {
    super(owner, args);

    let shouldLoadAsyncAutocompleteValues = args.supportedConditionTypes.some((type) =>
      ASYNC_AUTOCOMPLETE_TYPES.includes(type),
    );

    if (shouldLoadAsyncAutocompleteValues) {
      this.loadAsyncAutocompleteValues.perform();
    }
  }

  private getAsyncAutocompleteLoadTask(type: DataActionConditionType) {
    switch (type) {
      case DataActionConditionType.EventAttribute:
        return this.autosuggest.loadEventKeys.perform(this.args.campaign.event);
    }

    return null;
  }

  private extractTriggerAttributeAutocompleteValues() {
    if (this.args.campaign.isApiTriggered) {
      return extractBroadcastTriggerAutocompleteValues(this.args.selectedTrigger);
    }

    if (this.args.campaign.isWebhookTriggered) {
      return extractLogAutocompleteValues(this.args.selectedEvent);
    }

    return [];
  }

  private getSyncAutocompleteValues(type: DataActionConditionType) {
    switch (type) {
      case DataActionConditionType.TriggerAttribute:
        return this.extractTriggerAttributeAutocompleteValues();
      case DataActionConditionType.ProfileAttribute:
        return transformValuesToAutocompleteValues(this.environment.sentAttributes);
    }

    return [];
  }

  loadAsyncAutocompleteValues = task(async () => {
    let typesToLoad = ASYNC_AUTOCOMPLETE_TYPES.filter((t) => this.args.supportedConditionTypes.includes(t));

    let tasks = typesToLoad.reduce<Partial<Record<DataActionConditionType, Promise<string[]>>>>((acc, type) => {
      let task = this.getAsyncAutocompleteLoadTask(type);

      if (task == null) {
        return acc;
      }

      acc[type] = task;
      return acc;
    }, {});

    return hash(tasks);
  });

  get autocompleteValues(): Partial<Record<DataActionConditionType, any[]>> {
    let asyncTaskResults: Partial<Record<DataActionConditionType, string[]>> =
      this.loadAsyncAutocompleteValues.last?.value ?? {};

    return [
      DataActionConditionType.TriggerAttribute,
      DataActionConditionType.ProfileAttribute,
      DataActionConditionType.EventAttribute,
    ].reduce<Partial<Record<DataActionConditionType, any[]>>>((acc, type) => {
      let isAsyncType = ASYNC_AUTOCOMPLETE_TYPES.includes(type);

      if (isAsyncType) {
        let results = asyncTaskResults[type] ?? [];
        results = transformValuesToAutocompleteValues(results, this.args.splitEventAttributesByDot ?? false);
        acc[type] = results;
        return acc;
      }

      acc[type] = this.getSyncAutocompleteValues(type);
      return acc;
    }, {});
  }
}
